import { useRef, useState } from "react";
import { Play, X } from "lucide-react";
import { VIDEOS_VERTICAL, VIDEOS_WIDE } from "@/data/results";
import { slug } from "@/data/nav";
import { Reveal } from "@/components/site/Motion";
import { Label, H2, Paras } from "@/components/site/Blocks";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

const Tile = ({ v, aspect, onOpen, i }) => {
  const ref = useRef(null);
  const [on, setOn] = useState(false);
  const play = () => { setOn(true); const p = ref.current?.play(); if (p) p.catch(() => {}); };
  const stop = () => { setOn(false); if (ref.current) ref.current.pause(); };
  return (
    <Reveal delay={(i % 3) * 0.06}>
      <button type="button" data-testid={`video-tile-${slug(v.title)}`} onClick={() => onOpen(v)} onMouseEnter={play} onMouseLeave={stop}
        className={`group relative block w-full overflow-hidden rounded-3xl bg-[#140d26] ${aspect}`}>
        <img src={v.poster} alt={`${v.title} AI video still`} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
        <video ref={ref} src={v.src} muted loop playsInline preload="none" poster={v.poster} className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-500 ${on ? "opacity-100" : "opacity-0"}`} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0716]/90 via-transparent to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between p-5">
          <span className="font-display text-lg text-white sm:text-xl">{v.title}</span>
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur-md transition-[background-color] duration-300 group-hover:bg-violet-500"><Play className="h-4 w-4" fill="currentColor" /></span>
        </div>
      </button>
    </Reveal>
  );
};

export const VideoWork = () => {
  const [open, setOpen] = useState(null);
  return (
    <section data-testid="video-work-section" className="grain relative bg-ink py-24 text-white sm:py-32">
      <div className="wrap">
        <div className="grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <Reveal><Label n="02" dark>Perfect Digitals Studio</Label></Reveal>
            <Reveal delay={0.05}><H2 dark className="mt-6">AI Video <span className="font-serif-i text-gradient">Production Work</span></H2></Reveal>
          </div>
          <Reveal delay={0.1} className="lg:col-span-5 lg:col-start-8">
            <Paras dark items={["Perfect Digitals Studio is our specialist AI video production division, creating premium commercial video content using the latest generation of AI production technology."]} />
          </Reveal>
        </div>
        <div className="mt-14 grid gap-4 sm:grid-cols-3" data-testid="video-grid-vertical">
          {VIDEOS_VERTICAL.map((v, i) => <Tile key={v.title} v={v} i={i} aspect="aspect-[9/16]" onOpen={setOpen} />)}
        </div>
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="video-grid-wide">
          {VIDEOS_WIDE.map((v, i) => <Tile key={v.title} v={v} i={i} aspect="aspect-video" onOpen={setOpen} />)}
        </div>
      </div>
      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent data-testid="video-dialog" className="max-w-5xl border-white/10 bg-[#0B0716] p-3 sm:p-4 [&>button]:hidden">
          <DialogTitle className="flex items-center justify-between px-1 font-display text-lg text-white">
            {open?.title}
            <button type="button" data-testid="video-dialog-close" onClick={() => setOpen(null)} className="rounded-full p-2 text-slate-300 hover:bg-white/10 hover:text-white"><X className="h-4 w-4" /></button>
          </DialogTitle>
          {open && <video key={open.src} src={open.src} poster={open.poster} controls autoPlay playsInline className="max-h-[78vh] w-full rounded-2xl bg-black" />}
        </DialogContent>
      </Dialog>
    </section>
  );
};
